import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { landingAPI, productsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PREMIUM_TEMPLATES, NICHE_CATEGORIES } from '../lib/templates';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, Globe, Copy, ExternalLink, Eye, FileText, Loader2, Sparkles, Wand2, Check, Package, Users, Lock, Search, Grid3X3, Mic, BookOpen, Palette, Briefcase, Heart, Camera, TrendingUp, Star, ArrowLeft, Crown } from 'lucide-react';
import PlanLimitBanner from '../components/PlanLimitBanner';
import NoGatewayBanner from '../components/NoGatewayBanner';

const NICHE_ICONS = {
  all: Grid3X3,
};

const EDITOR_TYPES = [
  {
    id: 'advanced',
    label: 'Custom',
    description: 'Start from a template, add testimonials, FAQ, counters & more.',
    icon: Sparkles,
    tag: 'Recommended',
    available: true
  },
  {
    id: 'ai',
    label: 'AI Builder',
    description: 'Describe your business — AI creates a page with copywriting.',
    icon: Wand2,
    tag: 'Premium only',
    available: true,
    premium: true
  }
];

export default function LandingPagesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [selectedEditor, setSelectedEditor] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedNiche, setSelectedNiche] = useState('premium');
  const [templateSearch, setTemplateSearch] = useState('');
  const [newTitle, setNewTitle] = useState('My Store');
  const [creating, setCreating] = useState(false);
  // AI Builder state
  const [aiForm, setAiForm] = useState({
    store_name: '', product_name: '', product_type: '', nature_of_business: '', target_audience: '', additional_info: '',
    product_ids: [], language: 'en'
  });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [pagesRes, productsRes] = await Promise.all([
        landingAPI.list(),
        productsAPI.list()
      ]);
      setPages(pagesRes.data);
      setAllProducts(productsRes.data.filter(p => !p.is_bundle && p.is_published));
    } catch (err) {
      toast.error('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Filtered templates
  const filteredTemplates = PREMIUM_TEMPLATES.filter(t => {
    const matchNiche = selectedNiche === 'all' || t.niche === selectedNiche;
    const matchSearch = !templateSearch || t.name.toLowerCase().includes(templateSearch.toLowerCase()) || t.description.toLowerCase().includes(templateSearch.toLowerCase());
    return matchNiche && matchSearch;
  });

  const handleCreate = async () => {
    if (!selectedTemplate) { toast.error('Please select a template'); return; }
    setCreating(true);
    try {
      const tmpl = PREMIUM_TEMPLATES.find(t => t.id === selectedTemplate);
      if (!tmpl) { toast.error('Template not found'); return; }
      const createData = {
        title: newTitle || 'My Store',
        editor_type: 'advanced',
        template: tmpl.id,
        layout_type: tmpl.layout_type,
        ...tmpl.data,
      };
      const res = await landingAPI.create(createData);
      toast.success('Landing page created!');
      setCreateOpen(false);
      navigate(`/app/landing-pages/${res.data.id}/edit`);
    } catch (err) {
      toast.error('Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (pageId) => {
    try { await landingAPI.publish(pageId); toast.success('Published!'); loadData(); } catch { toast.error('Failed'); }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Delete this landing page?')) return;
    try { await landingAPI.delete(pageId); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  };

  const handleDuplicate = async (pageId) => {
    try { await landingAPI.duplicate(pageId); toast.success('Duplicated'); loadData(); } catch { toast.error('Failed'); }
  };

  const copyLink = (page) => {
    const slug = page.slug || page.id;
    const handle = user?.handle || '';
    const url = handle ? `${window.location.origin}/${handle}/${slug}` : `${window.location.origin}/store/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };

  const getPageUrl = (page) => {
    const slug = page.slug || page.id;
    const handle = user?.handle || '';
    return handle ? `/${handle}/${slug}` : `/store/${slug}`;
  };

  const openCreateDialog = () => {
    setStep(0);
    setSelectedEditor('advanced');
    setSelectedTemplate(null);
    setSelectedNiche('all');
    setTemplateSearch('');
    setNewTitle('My Store');
    setAiForm({ store_name: '', product_name: '', product_type: '', nature_of_business: '', target_audience: '', additional_info: '', product_ids: [], language: 'en' });
    setAiResult(null);
    setCreateOpen(true);
  };

  const handleAiGenerate = async () => {
    if (!aiForm.store_name.trim()) { toast.error('Please enter your store/brand name'); return; }
    if (!aiForm.nature_of_business.trim()) { toast.error('Please describe your business'); return; }
    if (aiForm.product_ids.length === 0) { toast.error('Please select at least one product'); return; }
    setAiGenerating(true);
    setAiResult(null);
    try {
      const parts = [`Store/Brand: ${aiForm.store_name.trim()}`];
      if (aiForm.product_name.trim()) parts.push(`Product Name: ${aiForm.product_name.trim()}`);
      if (aiForm.product_type) parts.push(`Product Type: ${aiForm.product_type}`);
      parts.push(`Nature of Business: ${aiForm.nature_of_business.trim()}`);
      if (aiForm.target_audience.trim()) parts.push(`Target Audience: ${aiForm.target_audience.trim()}`);
      if (aiForm.additional_info.trim()) parts.push(`Additional Notes: ${aiForm.additional_info.trim()}`);
      const res = await landingAPI.aiGenerate({
        business_description: parts.join('\n'),
        product_ids: aiForm.product_ids,
        language: aiForm.language,
      });
      setAiResult(res.data.page);
      toast.success('Landing page generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally { setAiGenerating(false); }
  };

  const handleAiAccept = () => {
    if (aiResult?.id) { setCreateOpen(false); navigate(`/app/landing-pages/${aiResult.id}/edit`); }
  };

  const handleAiRegenerate = async () => {
    if (aiResult?.id) { try { await landingAPI.delete(aiResult.id); } catch {} }
    setAiResult(null);
    handleAiGenerate();
  };

  const toggleAiProduct = (pid) => {
    setAiForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(pid) ? prev.product_ids.filter(i => i !== pid) : [...prev.product_ids, pid]
    }));
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-10 w-48 bg-muted animate-pulse rounded" /><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2].map(i => <div key={i} className="h-48 rounded-xl bg-muted animate-pulse" />)}</div></div>;
  }

  // Mini template preview — renders a realistic miniature of the actual page
  const TemplatePreview = ({ tmpl, isSelected, onClick }) => {
    const d = tmpl.data;
    const p = tmpl.preview || {};
    const gradient = p.gradient || d.hero_gradient || d.background_color;
    const accent = p.accent || d.primary_color;
    const textLight = p.textLight !== undefined ? p.textLight : false;
    const heroImg = p.heroImage || d.hero_image;
    const tc = textLight ? '#FFFFFF' : '#1C1917';
    const tcSub = textLight ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)';
    const hs = d.headline_style || {};
    const ss = d.subheadline_style || {};

    const handleView = (e) => { e.stopPropagation(); window.open(`/template-preview/${tmpl.id}`, '_blank'); };

    return (
      <div data-testid={`template-${tmpl.id}`} className={`group rounded-xl overflow-hidden text-left transition-all duration-300 ${isSelected ? 'ring-2 ring-primary ring-offset-2 shadow-xl scale-[1.02]' : 'hover:shadow-lg hover:scale-[1.01]'}`} style={{ background: '#fff' }}>
        {/* Realistic template preview */}
        <div className="aspect-[3/4] relative overflow-hidden" style={{ background: gradient }}>
          {/* Template-specific mini rendering */}
          {tmpl.layout_type === 'vivid' ? (
            <div className="h-full relative">
              {heroImg && <div className="absolute inset-0"><img src={heroImg} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,10,10,0.8), rgba(10,10,10,0.4))' }} /></div>}
              <div className="relative z-10 p-3 flex flex-col justify-center h-full">
                <div className="max-w-[75%]">
                  <div className="h-[3px] rounded-full mb-0.5 w-[90%]" style={{ backgroundColor: '#FFFFFF', opacity: 0.9 }} />
                  <div className="h-[3px] rounded-full mb-0.5 w-[70%]" style={{ backgroundColor: '#FFFFFF', opacity: 0.9 }} />
                  <div className="h-[3px] rounded-full mb-1.5 w-[50%]" style={{ backgroundColor: '#FFFFFF', opacity: 0.9 }} />
                  <div className="h-[2px] rounded-full mb-2.5 w-[65%]" style={{ backgroundColor: 'rgba(255,255,255,0.4)' }} />
                  <div className="h-4 w-16 rounded-full" style={{ backgroundColor: accent }} />
                </div>
              </div>
              {/* Mini features preview */}
              <div className="absolute bottom-0 left-0 right-0 p-2.5">
                <div className="grid grid-cols-3 gap-1">
                  {[1,2,3].map(i => <div key={i} className="p-1.5 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}><div className="text-[7px] font-black mb-0.5" style={{ color: `${accent}40` }}>{String(i).padStart(2,'0')}</div><div className="h-1 w-8 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} /></div>)}
                </div>
              </div>
            </div>
          ) : tmpl.layout_type === 'serene' ? (
            <div className="h-full flex flex-col items-center justify-center p-4 text-center">
              <div className="h-[3px] rounded-full mb-0.5 w-[65%]" style={{ backgroundColor: tc, opacity: 0.6, fontStyle: 'italic' }} />
              <div className="h-[3px] rounded-full mb-1.5 w-[45%]" style={{ backgroundColor: tc, opacity: 0.6 }} />
              <div className="flex items-center gap-1.5 mb-1.5"><div className="w-5 h-px" style={{ backgroundColor: `${tc}15` }} /><div className="h-1 w-1 rotate-45" style={{ backgroundColor: `${accent}50` }} /><div className="w-5 h-px" style={{ backgroundColor: `${tc}15` }} /></div>
              <div className="h-[2px] rounded-full mb-3 w-[55%]" style={{ backgroundColor: tcSub }} />
              <div className="h-3.5 w-20 rounded-none" style={{ border: `1px solid ${accent}`, opacity: 0.6 }} />
              {/* Mini features */}
              <div className="mt-4 space-y-1 w-[70%]">
                {[1,2,3].map(i => <div key={i} className="flex items-center gap-2 py-1" style={{ borderLeft: `1.5px solid ${accent}30` }}><div className="w-3 h-3 rounded-full ml-2" style={{ backgroundColor: `${accent}12` }} /><div className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: `${tc}08` }} /></div>)}
              </div>
            </div>
          ) : tmpl.layout_type === 'editorial' ? (
            <div className="h-full flex p-3">
              <div className="w-[58%] flex flex-col justify-center pr-2">
                <div className="h-[3px] rounded-full mb-0.5 w-[90%]" style={{ backgroundColor: tc, opacity: 0.8 }} />
                <div className="h-[3px] rounded-full mb-0.5 w-[65%]" style={{ backgroundColor: tc, opacity: 0.8 }} />
                <div className="h-[2px] rounded-full mb-2.5 w-[75%]" style={{ backgroundColor: tcSub }} />
                <div className="flex items-center gap-1"><div className="h-1.5 w-10 rounded-full" style={{ backgroundColor: accent, opacity: 0.6 }} /><span className="text-[5px]" style={{ color: accent }}>→</span></div>
                {/* 2-col features */}
                <div className="grid grid-cols-2 gap-1.5 mt-3">
                  {[1,2].map(i => <div key={i} className="text-center py-1.5"><div className="w-3 h-3 rounded-full mx-auto mb-0.5" style={{ backgroundColor: `${accent}15` }} /><div className="h-1 w-8 rounded-full mx-auto" style={{ backgroundColor: `${tc}10` }} /></div>)}
                </div>
              </div>
              <div className="w-[42%] rounded-md overflow-hidden">
                {heroImg ? <img src={heroImg} alt="" className="w-full h-full object-cover" style={{ opacity: 0.8 }} /> : <div className="w-full h-full" style={{ backgroundColor: `${accent}10` }} />}
              </div>
            </div>
          ) : tmpl.layout_type === 'construct' ? (
            <div className="h-full relative">
              {heroImg && <div className="absolute inset-0"><img src={heroImg} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.5), rgba(15,23,42,0.9))' }} /></div>}
              <div className="relative z-10 p-3 flex flex-col justify-center h-full">
                <div className="ml-auto max-w-[65%] text-right">
                  <div className="h-[3px] rounded-full mb-0.5 w-[85%] ml-auto" style={{ backgroundColor: '#F8FAFC', opacity: 0.85 }} />
                  <div className="h-[3px] rounded-full mb-1.5 w-[65%] ml-auto" style={{ backgroundColor: '#F8FAFC', opacity: 0.85 }} />
                  <div className="h-[2px] rounded-full mb-2.5 w-[80%] ml-auto" style={{ backgroundColor: 'rgba(148,163,184,0.4)' }} />
                  <div className="h-3.5 w-14 ml-auto" style={{ backgroundColor: accent }} />
                </div>
              </div>
              {/* Mini 2x2 features */}
              <div className="absolute bottom-0 left-0 right-0 p-2.5">
                <div className="grid grid-cols-2 gap-1">
                  {[1,2,3,4].map(i => <div key={i} className="p-1.5 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderLeft: `2px solid ${accent}60` }}><div className="h-1 w-8 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} /></div>)}
                </div>
              </div>
            </div>
          ) : tmpl.layout_type === 'playful' ? (
            <div className="h-full flex flex-col items-center justify-center p-4 text-center relative">
              <div className="absolute top-2 left-2 w-10 h-10 rounded-full opacity-20 blur-lg" style={{ backgroundColor: accent }} />
              <div className="absolute bottom-2 right-2 w-12 h-12 rounded-full opacity-15 blur-lg" style={{ backgroundColor: accent }} />
              <div className="relative z-10">
                <div className="h-[3px] rounded-full mb-0.5 w-28 mx-auto" style={{ backgroundColor: tc, opacity: 0.8 }} />
                <div className="h-[3px] rounded-full mb-1.5 w-20 mx-auto" style={{ backgroundColor: tc, opacity: 0.8 }} />
                <div className="h-[2px] rounded-full mb-3 w-24 mx-auto" style={{ backgroundColor: tcSub }} />
                <div className="h-4 w-16 rounded-xl mx-auto shadow-sm" style={{ backgroundColor: accent }} />
                {/* Rounded cards */}
                <div className="grid grid-cols-3 gap-1.5 mt-4">
                  {[1,2,3].map(i => <div key={i} className="p-1.5 rounded-lg bg-white shadow-sm text-center"><div className="w-3 h-3 rounded-md mx-auto mb-0.5" style={{ backgroundColor: `${accent}15` }} /><div className="h-1 w-6 rounded-full mx-auto" style={{ backgroundColor: `${tc}10` }} /></div>)}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col p-3 items-center justify-center">
              <div className="h-[3px] rounded-full mb-0.5 w-[70%]" style={{ backgroundColor: tc, opacity: 0.8 }} />
              <div className="h-[2px] rounded-full mb-2 w-[50%]" style={{ backgroundColor: tcSub }} />
              <div className="h-4 w-16 rounded-md" style={{ backgroundColor: accent }} />
            </div>
          )}

          {isSelected && (
            <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
          )}
        </div>

        {/* Info + Action Buttons */}
        <div className="p-3 bg-card">
          <div className="flex items-center gap-1.5 mb-1">
            <p className="text-xs font-semibold truncate flex-1">{tmpl.name}</p>
            <span className="text-[8px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">{tmpl.layout_type}</span>
          </div>
          <p className="text-[10px] text-muted-foreground mb-3 line-clamp-2">{tmpl.description}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={handleView} data-testid={`template-view-${tmpl.id}`}>
              <Eye className="h-3 w-3 mr-1" /> View
            </Button>
            <Button size="sm" className="flex-1 h-8 text-xs" onClick={onClick} data-testid={`template-use-${tmpl.id}`}>
              <Check className="h-3 w-3 mr-1" /> Use
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Landing Pages</h1>
          <p className="text-sm text-muted-foreground mt-1">{pages.length} page{pages.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="landing-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Page
        </Button>
      </div>

      <PlanLimitBanner resource="landing_pages" label="landing pages" />
      <NoGatewayBanner ctaLabel="Enable checkout" />

      {pages.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold">No landing pages yet</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create a landing page with built-in checkout to start selling</p>
            <Button onClick={openCreateDialog}><Plus className="h-4 w-4 mr-1.5" /> Create Page</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(page => (
            <Card key={page.id} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg truncate">{page.title}</h3>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <Badge variant="secondary" className="text-xs capitalize">
                        {page.template === 'ai-generated' || page.ai_generated ? 'AI Builder' : 'Custom'}
                      </Badge>
                      {page.is_published ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs"><Globe className="h-3 w-3 mr-1" /> Live</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Draft</Badge>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/app/landing-pages/${page.id}/edit`)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(page.id)}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyLink(page)}><ExternalLink className="h-4 w-4 mr-2" /> Copy Link</DropdownMenuItem>
                      {page.is_published && <DropdownMenuItem onClick={() => window.open(getPageUrl(page), '_blank')}><ExternalLink className="h-4 w-4 mr-2" /> View Live</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => handleDelete(page.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1 mb-3">{page.headline}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                  <span className="flex items-center gap-1"><Package className="h-3 w-3" /> {(page.product_ids || []).length} products</span>
                  <span>&middot;</span>
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {(page.total_views || 0).toLocaleString()} views</span>
                  <span>&middot;</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {(page.unique_visitors || 0).toLocaleString()} unique</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/app/landing-pages/${page.id}/edit`)}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                  {page.is_published ? (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => window.open(getPageUrl(page), '_blank')}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                  ) : (
                    <Button size="sm" className="flex-1" onClick={() => handlePublish(page.id)}><Globe className="h-3.5 w-3.5 mr-1" /> Publish</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className={step === 1 && selectedEditor !== 'ai' ? 'max-w-4xl max-h-[90vh] overflow-hidden flex flex-col' : step === 1 && selectedEditor === 'ai' ? 'max-w-2xl' : 'max-w-lg'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {step > 0 && (
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { setStep(0); setAiResult(null); setSelectedTemplate(null); }}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              {step === 0 ? 'Create Landing Page' : selectedEditor === 'ai' ? 'AI Builder' : 'Choose a Template'}
            </DialogTitle>
          </DialogHeader>

          {step === 0 ? (
            /* Step 0: Editor Type Selection */
            <div className="space-y-3" data-testid="editor-type-picker">
              {EDITOR_TYPES.map(type => {
                const Icon = type.icon;
                const isSelected = selectedEditor === type.id;
                const isPremiumLocked = type.premium && user?.plan !== 'premium';
                return (
                  <button
                    key={type.id}
                    onClick={() => { if (type.available && !isPremiumLocked) setSelectedEditor(type.id); }}
                    disabled={isPremiumLocked}
                    className={`relative w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
                      ${isPremiumLocked ? 'opacity-50 cursor-not-allowed border-border' : isSelected ? 'border-primary bg-accent shadow-sm' : 'border-border hover:border-primary/30'}`}
                    data-testid={`editor-type-${type.id}`}
                  >
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Icon className={`h-6 w-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{type.label}</p>
                        <Badge variant="secondary" className="text-[10px]">{type.tag}</Badge>
                        {isPremiumLocked && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                      </div>
                      <p className="text-sm mt-0.5 text-muted-foreground">{type.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : selectedEditor === 'ai' ? (
            /* AI Builder Form */
            <div className="space-y-4" data-testid="ai-builder-form">
              {!aiResult ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Store / Brand Name <span className="text-destructive">*</span></Label>
                      <Input value={aiForm.store_name} onChange={e => setAiForm(p => ({ ...p, store_name: e.target.value }))} placeholder="e.g. Sarah's Design Studio" disabled={aiGenerating} data-testid="ai-store-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Product Name</Label>
                      <Input value={aiForm.product_name} onChange={e => setAiForm(p => ({ ...p, product_name: e.target.value }))} placeholder="e.g. Lightroom Preset Pack" disabled={aiGenerating} data-testid="ai-product-name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Type of Product</Label>
                      <Select value={aiForm.product_type} onValueChange={v => setAiForm(p => ({ ...p, product_type: v }))} disabled={aiGenerating}>
                        <SelectTrigger data-testid="ai-product-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          {['ebook', 'course', 'template', 'digital_art', 'audio', 'video', 'software', 'photography', 'coaching', 'other'].map(v => (
                            <SelectItem key={v} value={v}>{v.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Target Audience</Label>
                      <Input value={aiForm.target_audience} onChange={e => setAiForm(p => ({ ...p, target_audience: e.target.value }))} placeholder="e.g. Freelance photographers" disabled={aiGenerating} data-testid="ai-target-audience" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nature of Business <span className="text-destructive">*</span></Label>
                    <Textarea value={aiForm.nature_of_business} onChange={e => setAiForm(p => ({ ...p, nature_of_business: e.target.value }))} placeholder="Describe what you sell and what makes it special..." rows={2} disabled={aiGenerating} data-testid="ai-nature-business" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Additional Info <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                    <Textarea value={aiForm.additional_info} onChange={e => setAiForm(p => ({ ...p, additional_info: e.target.value }))} placeholder="e.g. Use a warm, friendly tone. Minimalist design." rows={2} disabled={aiGenerating} data-testid="ai-additional-info" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Products to Feature ({aiForm.product_ids.length} selected) <span className="text-destructive">*</span></Label>
                    <div className="space-y-1.5 max-h-[120px] overflow-y-auto border rounded-lg p-2">
                      {allProducts.length > 0 ? allProducts.map(p => (
                        <label key={p.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${aiForm.product_ids.includes(p.id) ? 'bg-accent' : 'hover:bg-muted/60'}`}>
                          <input type="checkbox" checked={aiForm.product_ids.includes(p.id)} onChange={() => toggleAiProduct(p.id)} disabled={aiGenerating} className="accent-[hsl(var(--primary))] h-4 w-4" />
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{p.name}</p></div>
                          <Badge variant="secondary" className="text-[10px]">{p.type}</Badge>
                          <span className="text-xs font-semibold">RM {parseFloat(p.price || 0).toFixed(2)}</span>
                        </label>
                      )) : <p className="text-sm text-muted-foreground text-center py-4">No published products yet.</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Content Language</Label>
                    <Select value={aiForm.language} onValueChange={v => setAiForm(p => ({ ...p, language: v }))} disabled={aiGenerating}>
                      <SelectTrigger data-testid="ai-builder-language"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="ms">Bahasa Melayu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                /* AI Result Preview */
                <div className="space-y-4" data-testid="ai-builder-preview">
                  <div className="rounded-xl border overflow-hidden">
                    <div className="p-5 space-y-3" style={{ backgroundColor: aiResult.background_color || '#F5E9CF' }}>
                      <div className="text-center space-y-2">
                        <h3 className="text-lg font-bold" style={{ color: aiResult.text_color || '#0F172A' }}>{aiResult.headline}</h3>
                        <p className="text-sm" style={{ color: `${aiResult.text_color || '#0F172A'}99` }}>{aiResult.subheadline}</p>
                      </div>
                      <div className="flex items-center justify-center gap-2 pt-2">
                        <div className="h-8 px-4 rounded-lg flex items-center justify-center text-xs font-medium text-white" style={{ backgroundColor: aiResult.primary_color || '#195259' }}>
                          {aiResult.custom_cta_text || 'Get Instant Access'}
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-card border-t space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold">{aiResult.title}</p>
                        <Badge variant="secondary" className="text-[10px]">AI Generated</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {(aiResult.body_sections || []).length} sections &middot; {(aiResult.testimonials || []).length} testimonials &middot; {(aiResult.faq_items || []).length} FAQs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-accent/50 border border-primary/20">
                    <Wand2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">Not happy? Regenerate or proceed to editor to fine-tune manually.</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Template Gallery with Niche Browsing */
            <div className="flex flex-col overflow-hidden flex-1 min-h-0">
              {/* Title input */}
              <div className="space-y-2 mb-4 shrink-0">
                <Label>Page Title</Label>
                <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="My Store" data-testid="landing-title-input" />
              </div>

              <p className="text-sm text-muted-foreground mb-3 shrink-0">Pick a starting template. You can customize everything in the editor.</p>

              {/* Template grid — 5 premium templates */}
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pb-2">
                  {filteredTemplates.map(tmpl => (
                    <TemplatePreview
                      key={tmpl.id}
                      tmpl={tmpl}
                      isSelected={selectedTemplate === tmpl.id}
                      onClick={() => { setSelectedTemplate(tmpl.id); }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="shrink-0">
            {step === 0 ? (
              <Button onClick={() => setStep(1)} data-testid="editor-continue-btn">Continue</Button>
            ) : selectedEditor === 'ai' ? (
              aiResult ? (
                <div className="flex items-center gap-2 w-full justify-between">
                  <Button variant="outline" onClick={() => setAiResult(null)} data-testid="ai-builder-edit-prompt">Edit Prompt</Button>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={handleAiRegenerate} disabled={aiGenerating} data-testid="ai-builder-regenerate">
                      {aiGenerating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Regenerate
                    </Button>
                    <Button onClick={handleAiAccept} data-testid="ai-builder-accept"><Check className="h-4 w-4 mr-1.5" /> Edit in Editor</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={handleAiGenerate} disabled={aiGenerating || aiForm.product_ids.length === 0 || !aiForm.store_name.trim() || !aiForm.nature_of_business.trim()} data-testid="ai-builder-generate">
                  {aiGenerating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating...</> : <><Wand2 className="h-4 w-4 mr-1.5" /> Generate Landing Page</>}
                </Button>
              )
            ) : (
              <Button onClick={handleCreate} disabled={creating || !selectedTemplate} data-testid="create-landing-btn">
                {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Use Template
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
